import { RecentAssetCard } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/assets/RecentAssetCard'
import { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import {
  AssetSearchItem,
  ChartSearchItem,
  isAssetSearchItem,
  isChartSearchItem,
  isDocumentSearchItem,
  isSensorSearchItem,
  RecentItem,
  searchItemToDocumentTableRow,
  SensorSearchItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import React from 'react'
import styled from 'styled-components'

import { ExpandedDocumentCard } from './ExpandedDocumentCard'
import { RecentChartCard } from './RecentChartCard'
import { RecentSensorCard } from './RecentSensorCard'

const Container = styled.div<{ $fullPage?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: ${(props) => (props.$fullPage ? '0rem' : '0 1rem 0.75rem 1rem')};
  ${(props) =>
    !props.$fullPage &&
    `border-bottom: 1px solid ${props.theme.forecast.border};`}
  flex-shrink: 0;
  ${(props) => props.$fullPage && 'flex: 1;'}
`

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  max-height: min(29.25rem, 100%);
  flex: 1;
  min-width: 0;
`

interface RecentItemsProps {
  recentItems: RecentItem[]
  selectedItemId?: string | null
  onSelectDocument: (
    document: DocumentTableRow,
    event?: React.MouseEvent
  ) => void
  onSelectSensor: (sensor: SensorSearchItem, event?: React.MouseEvent) => void
  onSelectAsset?: (asset: AssetSearchItem, event?: React.MouseEvent) => void
  onSelectChart?: (chart: ChartSearchItem, event?: React.MouseEvent) => void
  onContextMenu?: (document: DocumentTableRow, event: React.MouseEvent) => void
  isFavorited?: (id: string) => boolean
  onToggleFavorite?: (document: DocumentTableRow) => void
  fullPage?: boolean
}

export const RecentItems: React.FC<RecentItemsProps> = ({
  recentItems,
  selectedItemId,
  onSelectDocument,
  onSelectSensor,
  onSelectAsset,
  onSelectChart,
  onContextMenu,
  isFavorited,
  onToggleFavorite,
  fullPage = false,
}) => {
  if (recentItems.length === 0) {
    return null
  }

  return (
    <Container $fullPage={fullPage}>
      <ScrollContainer>
        {recentItems.map((recent) => {
          if (isDocumentSearchItem(recent.item)) {
            const docRow = searchItemToDocumentTableRow(recent.item)
            return (
              <ExpandedDocumentCard
                key={recent.item.id}
                document={docRow}
                isSelected={recent.item.id === selectedItemId}
                isFavorited={isFavorited?.(recent.item.id)}
                onClick={onSelectDocument}
                onContextMenu={onContextMenu}
                onToggleFavorite={onToggleFavorite}
              />
            )
          }

          if (isSensorSearchItem(recent.item)) {
            return (
              <RecentSensorCard
                key={recent.item.id}
                sensor={recent.item}
                isSelected={recent.item.id === selectedItemId}
                onClick={onSelectSensor}
              />
            )
          }

          if (isAssetSearchItem(recent.item) && onSelectAsset) {
            return (
              <RecentAssetCard
                key={recent.item.id}
                asset={recent.item}
                isSelected={recent.item.id === selectedItemId}
                onClick={onSelectAsset}
              />
            )
          }

          if (isChartSearchItem(recent.item) && onSelectChart) {
            return (
              <RecentChartCard
                key={recent.item.id}
                chart={recent.item}
                isSelected={recent.item.id === selectedItemId}
                onClick={onSelectChart}
              />
            )
          }

          return null
        })}
      </ScrollContainer>
    </Container>
  )
}
